import React, { Component } from 'react';
import PropTypes from 'prop-types';

import http from '@/utils/http';

class MagicLinkForm extends Component {
  constructor(props) {
    super(props);

    const { link, productVariants } = props;

    const savedQty = link?.qty != null ? String(link.qty) : '';
    let qtyIsCustom = false;
    if (savedQty && link?.product_variant_id) {
      const variant = productVariants.find((pv) => pv.id === link.product_variant_id);
      const presets = (variant?.price_thresholds || []).map((pt) => String(pt.min_quantity));
      qtyIsCustom = !presets.includes(savedQty);
    }

    this.state = {
      selectedDrug: link ? link.drug : null,
      productVariantId: link ? link.product_variant_id : '',
      notes: link ? (link.notes || '') : '',
      qty: savedQty,
      qtyIsCustom,
      useDefault: link ? link.expiry_type !== 'custom' : true,
      customExpiresAt: link?.expires_at
        ? new Date(link.expires_at).toISOString().slice(0, 10)
        : '',
      processing: false,
      errors: {},
    };

    this.dateInputRef = React.createRef();
  }

  getUsedVariantIds = () => {
    const { magicLinks, link } = this.props;
    const editingVariantId = link ? link.product_variant_id : null;

    return new Set(
      magicLinks
        .filter((ml) => ml.status === 'active' && ml.product_variant_id !== editingVariantId)
        .map((ml) => ml.product_variant_id)
    );
  };

  getDrugVariants = (drug) => {
    const { productVariants } = this.props;
    const usedVariantIds = this.getUsedVariantIds();

    return productVariants.filter(
      (pv) =>
        pv.product_name &&
        pv.product_name.toLowerCase() === drug.toLowerCase() &&
        !usedVariantIds.has(pv.id)
    );
  };

  getAvailableDrugs = () => {
    const { productVariants } = this.props;
    const usedVariantIds = this.getUsedVariantIds();
    const drugs = ['sildenafil', 'tadalafil'];

    return drugs.map((drug) => ({
      value: drug,
      label: drug.charAt(0).toUpperCase() + drug.slice(1),
      disabled: !productVariants.some(
        (pv) =>
          pv.product_name &&
          pv.product_name.toLowerCase() === drug &&
          !usedVariantIds.has(pv.id)
      ),
    }));
  };

  handleDrugChange = (drug) => {
    this.setState({
      selectedDrug: drug,
      productVariantId: '',
      qty: '',
      qtyIsCustom: false,
    });
  };

  handleVariantChange = (e) => {
    this.setState({ productVariantId: Number(e.target.value), qty: '', qtyIsCustom: false });
  };

  handleNotesChange = (e) => {
    this.setState({ notes: e.target.value });
  };

  handleSubmit = (e) => {
    e.preventDefault();

    const { userId, link, onSuccessCreate, onSuccessUpdate } = this.props;
    const { selectedDrug, productVariantId, notes, qty, useDefault, customExpiresAt } = this.state;

    if (!selectedDrug) {
      this.setState({ errors: { drug: ['Please select a drug.'] } });
      return;
    }

    if (!productVariantId) {
      this.setState({ errors: { product_variant_id: ['Please select a strength.'] } });
      return;
    }

    if (!useDefault && !customExpiresAt) {
      this.setState({ errors: { expires_at: ['Please set a custom expiration date.'] } });
      return;
    }

    this.setState({ processing: true, errors: {} });

    const isEditing = link !== null;
    const payload = { magic_link: { product_variant_id: productVariantId, notes, expiry_type: useDefault ? 'default' : 'custom' } };
    if (!useDefault && customExpiresAt) {
      const offsetMins = -new Date().getTimezoneOffset();
      const sign = offsetMins >= 0 ? '+' : '-';
      const hh = String(Math.floor(Math.abs(offsetMins) / 60)).padStart(2, '0');
      const mm = String(Math.abs(offsetMins) % 60).padStart(2, '0');
      payload.magic_link.expires_at = `${customExpiresAt}T23:59:59${sign}${hh}:${mm}`;
    }
    const parsedQty = parseInt(qty, 10);
    if (qty.trim() !== '' && parsedQty > 0) payload.magic_link.qty = parsedQty;

    const request = isEditing
      ? http.put(`/magic_links/${link.id}?user_id=${userId}`, payload)
      : http.post(`/magic_links?user_id=${userId}`, payload);

    request
      .then((response) => {
        window.flashMessages.addMessage({
          type: 'success',
          text: isEditing ? 'Magic link updated.' : 'Magic link created.',
        });

        if (isEditing) {
          onSuccessUpdate(response.data);
        } else {
          onSuccessCreate(response.data);
        }
      })
      .catch((error) => {
        const errors = error.response?.data?.errors || {};
        this.setState({ errors });
        window.flashMessages.addMessage({ type: 'error', text: 'Failed to save magic link.' });
      })
      .finally(() => this.setState({ processing: false }));
  };

  render() {
    const { onCancel, link } = this.props;
    const { selectedDrug, productVariantId, notes, qty, qtyIsCustom, useDefault, customExpiresAt, processing, errors } = this.state;
    const { productVariants } = this.props;
    const variants = selectedDrug ? this.getDrugVariants(selectedDrug) : [];
    const selectedVariant = productVariants.find((pv) => pv.id === productVariantId);
    const presetQtys = (selectedVariant?.price_thresholds || [])
      .map((pt) => pt.min_quantity)
      .filter(Boolean)
      .sort((a, b) => a - b);
    const availableDrugs = this.getAvailableDrugs();
    const isEditing = link !== null;
    const isLocked = isEditing && link.editable === false;
    const canSubmit = !!selectedDrug && !!productVariantId && (useDefault || !!customExpiresAt);

    return (
      <form onSubmit={this.handleSubmit}>
        <div className="form-group">
          <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>Drug</label>
          <select
            className="form-control form-control-sm"
            value={selectedDrug || ''}
            onChange={(e) => this.handleDrugChange(e.target.value || null)}
            disabled={isLocked}
          >
            <option value="">Select drug...</option>
            {availableDrugs.map(({ value, label, disabled }) => (
              <option key={value} value={value} disabled={!isEditing && disabled}>
                {label}{!isEditing && disabled ? ' (no strengths available)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>Strength</label>
          <select
            className={`form-control form-control-sm ${errors.product_variant_id ? 'is-invalid' : ''}`}
            value={productVariantId}
            onChange={this.handleVariantChange}
            disabled={isLocked}
          >
            <option value="">Select strength...</option>
            {variants.map((pv) => (
              <option key={pv.id} value={pv.id}>
                {pv.product_name} {pv.dosage}
              </option>
            ))}
          </select>
          {errors.product_variant_id && (
            <div className="invalid-feedback">{errors.product_variant_id[0]}</div>
          )}
        </div>

        <div className="form-group">
          <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>
            Quantity <span style={{ fontWeight: '400', color: '#6c757d', fontSize: '0.8rem' }}>(optional)</span>
          </label>
          {productVariantId ? (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
                {presetQtys.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className={`btn btn-sm ${!qtyIsCustom && qty === String(q) ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: '0.75rem', padding: '2px 10px' }}
                    onClick={() => this.setState({ qty: String(q), qtyIsCustom: false })}
                  >
                    {q}
                  </button>
                ))}
                <button
                  type="button"
                  className={`btn btn-sm ${qtyIsCustom ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '0.75rem', padding: '2px 10px' }}
                  onClick={() => this.setState({ qtyIsCustom: true, qty: '' })}
                >
                  Custom
                </button>
              </div>
              {qtyIsCustom && (
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={qty}
                  min={1}
                  placeholder="Enter quantity..."
                  onChange={(e) => this.setState({ qty: e.target.value })}
                  autoFocus
                />
              )}
            </div>
          ) : (
            <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: 0 }}>
              Select a strength first to see available quantities.
            </p>
          )}
        </div>

        <div className="form-group">
          <label style={{ fontSize: '0.875rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem' }}>Expiration</label>
          <div className="d-flex align-items-center" style={{ gap: '1.25rem', marginBottom: '0.4rem' }}>
            <label
              className="mb-0 d-flex align-items-center"
              style={{ gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '400', userSelect: 'none' }}
            >
              <input
                type="radio"
                name="expiry-type"
                checked={useDefault}
                onChange={() => this.setState({ useDefault: true, customExpiresAt: '' })}
                style={{ cursor: 'pointer', width: '14px', height: '14px', marginTop: '1px' }}
              />
              Default (24 hours)
            </label>
            <label
              className="mb-0 d-flex align-items-center"
              style={{ gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '400', userSelect: 'none' }}
            >
              <input
                type="radio"
                name="expiry-type"
                checked={!useDefault}
                onChange={() => this.setState({ useDefault: false })}
                style={{ cursor: 'pointer', width: '14px', height: '14px', marginTop: '1px' }}
              />
              Custom
            </label>
          </div>
          <div>
            <div
              style={{ cursor: useDefault ? 'default' : 'pointer' }}
              onClick={() => { if (!useDefault && this.dateInputRef.current) this.dateInputRef.current.showPicker(); }}
            >
              <input
                ref={this.dateInputRef}
                type="date"
                className={`form-control form-control-sm ${errors.expires_at ? 'is-invalid' : ''}`}
                value={customExpiresAt}
                onChange={(e) => this.setState({ customExpiresAt: e.target.value })}
                disabled={useDefault}
                min={new Date().toISOString().slice(0, 10)}
                style={{ opacity: useDefault ? 0.45 : 1, transition: 'opacity 0.15s', cursor: useDefault ? 'default' : 'pointer', pointerEvents: 'none' }}
              />
            </div>
            {errors.expires_at && (
              <div className="invalid-feedback d-block">{errors.expires_at[0]}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>Notes</label>
          <textarea
            className="form-control form-control-sm"
            rows={3}
            value={notes}
            onChange={this.handleNotesChange}
            placeholder="Optional notes..."
          />
        </div>

        <div className="d-flex justify-content-end" style={{ gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-sm btn-primary"
            disabled={processing || !canSubmit}
          >
            {processing ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    );
  }
}

MagicLinkForm.propTypes = {
  userId: PropTypes.number.isRequired,
  productVariants: PropTypes.array.isRequired,
  magicLinks: PropTypes.array.isRequired,
  link: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSuccessCreate: PropTypes.func.isRequired,
  onSuccessUpdate: PropTypes.func.isRequired,
};

MagicLinkForm.defaultProps = {
  link: null,
};

export default MagicLinkForm;
